import angular from 'angular'
import { ApiResponse } from '@vend/types'

import { extractData } from '../../../util/extract-data'
import { AtLeast } from '../../../util/types'
import { $http } from '../../../ng-compat/imports'
import { ApiNode } from '../utils/qk-types'

const buildUrl = (buttonLayoutId: string, quickKeyId?: string) => {
  const url = `/api/2.0/button_layouts/${buttonLayoutId}/nodes`
  return quickKeyId ? `${url}/${quickKeyId}` : url
}

/**
 * Resource for querying the quick key nodes API (/api/2.0/button_layouts/:id:/nodes).
 */
export class QuickKeyNodesResource {
  /**
   * Delete the provided quick key from the given button layout.
   *
   * @param  buttonLayoutId
   *         The ID of the button layout containing the quick key to be deleted
   *
   * @param  quickKeyId
   *         The ID of the quick key to be deleted
   *
   * @return A promise resolved once the quick key has been deleted, or rejected if an error
   *         occurred in the process
   */
  public deleteQuickKey(buttonLayoutId: string, quickKeyId: string) {
    const url = buildUrl(buttonLayoutId, quickKeyId)
    return $http.delete<void>(url)
  }

  /**
   * Create a new quick key node with the given data on the given button layout.
   *
   * @param  buttonLayoutId
   *         The ID of the button layout to create the quick key on
   *
   * @param  quickKeyData
   *         The quick key data to create the quick key from
   *
   * @return A promise resolved with the newly created quick key node or rejected if an error
   *         occurred in the process
   */
  public createQuickKey(buttonLayoutId: string, quickKeyData: ApiNode) {
    const url = buildUrl(buttonLayoutId)
    return $http.post<ApiResponse<ApiNode>>(url, quickKeyData).then(extractData)
  }

  /**
   * Updates an existing quick key node with the properties provided.
   *
   * @param  buttonLayoutId
   *         The ID of the button layout that the quick key update occurs
   *
   * @param  quickKeyData
   *         The properties which should be updated. 'id' is required.
   *
   * @return A promise resolved with the updated node or rejected if an error occured in the process
   */
  public updateQuickKey(
    buttonLayoutId: string,
    quickKeyData: AtLeast<ApiNode, 'id'>
  ) {
    const { id, ...data } = quickKeyData

    const url = buildUrl(buttonLayoutId, quickKeyData.id)
    return $http.put<ApiResponse<ApiNode>>(url, data).then(extractData)
  }
}

angular
  .module('vendQuickKeys')
  .factory('qkNodesResource', QuickKeyNodesResource)
